import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header7 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template7' },
        { label: 'Shop', path: '/template7/shop' },
        { label: 'About', path: '/template7/about' },
        { label: 'Contact', path: '/template7/contact' },
        { label: 'Orders', path: '/template7/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Inter:wght@300;400;500&display=swap');
                .chandni-desktop-nav { display: none; }
                .chandni-mobile-toggle { display: flex; }
                @media (min-width: 1024px) {
                    .chandni-desktop-nav { display: flex; }
                    .chandni-mobile-toggle { display: none; }
                }
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderBottom: scrolled ? '1px solid #E5E7EB' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '80px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template7')}
                        style={{
                            fontFamily: "'Lora', Georgia, serif",
                            fontSize: '26px',
                            fontWeight: 600,
                            color: '#6366F1',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                        }}
                    >
                        CHANDNI
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '36px',
                        }}
                        className="chandni-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Lora', Georgia, serif",
                                    fontSize: '15px',
                                    fontWeight: 400,
                                    color: '#1E1B4B',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    transition: 'color 0.3s ease',
                                    textTransform: 'capitalize',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#6366F1')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1E1B4B')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1E1B4B', cursor: 'pointer', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#6366F1')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1E1B4B')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template7/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1E1B4B', transition: 'color 0.3s' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#6366F1')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1E1B4B')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        width: '18px',
                                        height: '18px',
                                        backgroundColor: '#6366F1',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>
                        <User
                            size={20}
                            style={{ color: '#1E1B4B', cursor: 'pointer', transition: 'color 0.3s' }}
                            onClick={() => navigate('/template7/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#6366F1')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1E1B4B')}
                        />

                        {/* Mobile Menu Toggle */}
                        <button
                            className="chandni-mobile-toggle"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {mobileMenuOpen ? (
                                <X size={24} style={{ color: '#1E1B4B' }} />
                            ) : (
                                <Menu size={24} style={{ color: '#1E1B4B' }} />
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 49,
                        backgroundColor: 'rgba(30, 27, 75, 0.95)',
                        backdropFilter: 'blur(20px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '32px',
                    }}
                >
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Lora', Georgia, serif",
                                fontSize: '28px',
                                fontWeight: 400,
                                color: '#FFFFFF',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                letterSpacing: '3px',
                                textTransform: 'capitalize',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#A5B4FC')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                        >
                            {link.label}
                        </button>
                    ))}
                    <div style={{ marginTop: '24px', display: 'flex', gap: '24px' }}>
                        <button
                            onClick={() => handleNavClick('/template7/login')}
                            style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '14px',
                                fontWeight: 400,
                                color: '#A5B4FC',
                                background: 'none',
                                border: '1px solid #A5B4FC',
                                borderRadius: '4px',
                                padding: '10px 28px',
                                cursor: 'pointer',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                            }}
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header7;
